/**
 * v3.21-1760 · FinancingResultModal
 *
 * Focused financing-result workspace opened by clicking a Client name in
 * the active Clients/Pipeline list (`pages/ClientsPage.js`).
 *
 * Consumes the authoritative v3.21-1757 endpoints:
 *   GET  /api/finturf/offers/{client_id}          (cached / synced state)
 *   GET  /api/finturf/offers/{client_id}/refresh  (live upstream refresh)
 *
 * Renders exactly one of the five canonical result states from the
 * server-side classifier SSOT (services/finturf_result_classifier.py):
 *
 *   NOT_SUBMITTED    — Hub has no Fitnurf identifier for this Client
 *   PROCESSING       — Fitnurf accepted the application, decision pending
 *   VALID_OFFER      — at least one offer with approved_amount > 0
 *   NO_OFFER_FOUND   — Fitnurf completed with no offer (NOT an error)
 *   SUBMISSION_ERROR — technical / lender-availability failure
 *
 * Per-Client scope
 * ----------------
 * The modal is fully self-contained — every fetch keys off the `clientId`
 * prop and uses local (not global) React state.  Opening this modal for
 * Client A does NOT block work on Client B in the underlying list; each
 * row remains independently interactive.  Closing the modal or navigating
 * away NEVER cancels upstream Fitnurf processing — the Hub backend
 * persists `finturf_submissions` per submit attempt and its sync worker
 * keeps state fresh regardless of FE lifecycle.
 *
 * External partner privacy is preserved SERVER-SIDE by
 * `services.lender_identity_scrub.maybe_scrub` on both endpoints; this
 * modal renders exactly whatever the server returned.  No lender identity
 * is ever reconstructed client-side.
 */
import React, { useCallback, useEffect, useState } from 'react';
import api from '../lib/api';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  RefreshCw,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Clock,
  FileText,
} from 'lucide-react';

const STATE_STYLES = {
  VALID_OFFER:      { label: 'Offers Available', badge: 'bg-emerald-100 text-emerald-800 border-emerald-200', Icon: CheckCircle2 },
  NO_OFFER_FOUND:   { label: 'No Offer Found',   badge: 'bg-slate-100 text-slate-700 border-slate-200',       Icon: XCircle },
  SUBMISSION_ERROR: { label: 'Submission Issue', badge: 'bg-amber-100 text-amber-800 border-amber-300',       Icon: AlertTriangle },
  PROCESSING:       { label: 'Processing',       badge: 'bg-sky-100 text-sky-800 border-sky-200',             Icon: Clock },
  NOT_SUBMITTED:    { label: 'Not Submitted',    badge: 'bg-slate-100 text-slate-600 border-slate-200',       Icon: FileText },
};

const money = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  const n = typeof v === 'string' ? parseFloat(v.replace(/[$,]/g, '')) : Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
};

function OfferCard({ index, offer }) {
  return (
    <div
      className="rounded-lg border border-slate-200 bg-white p-4 space-y-2"
      data-testid={`financing-offer-card-${index}`}
    >
      <div className="flex items-center justify-between">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
          Offer {index}
        </div>
        {offer.lender_name && (
          <div className="text-[11px] font-medium text-slate-700" data-testid={`financing-offer-lender-${index}`}>
            {offer.lender_name}
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
        <div>
          <div className="text-[10px] text-slate-500 uppercase">Approved Amount</div>
          <div className="font-semibold text-emerald-700" data-testid={`financing-offer-amount-${index}`}>
            {money(offer.approved_amount ?? offer.approvedAmount ?? offer.amount)}
          </div>
        </div>
        <div>
          <div className="text-[10px] text-slate-500 uppercase">Term</div>
          <div className="font-medium text-slate-800">
            {offer.term_display || (offer.term ? `${offer.term} months` : '—')}
          </div>
        </div>
        <div>
          <div className="text-[10px] text-slate-500 uppercase">Monthly Payment</div>
          <div className="font-medium text-slate-800">
            {money(offer.monthly_payment ?? offer.monthlyPayment ?? offer.payment)}
          </div>
        </div>
        <div>
          <div className="text-[10px] text-slate-500 uppercase">APR</div>
          <div className="font-medium text-slate-800">
            {offer.apr ? `${offer.apr}%` : (offer.rate ? `${offer.rate}%` : '—')}
          </div>
        </div>
      </div>
      {offer.status && (
        <div className="text-[11px] text-slate-500 pt-1">
          Status: <span className="text-slate-700">{offer.status}</span>
        </div>
      )}
    </div>
  );
}

export default function FinancingResultModal({ clientId, clientName, open, onClose }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (mode) => {
    if (!clientId) return;
    (mode === 'refresh' ? setRefreshing : setLoading)(true);
    setError(null);
    try {
      const url = mode === 'refresh'
        ? `/finturf/offers/${clientId}/refresh`
        : `/finturf/offers/${clientId}`;
      const { data: resp } = await api.get(url);
      setData(resp);
    } catch (e) {
      const msg = e?.response?.data?.detail?.message
                || e?.response?.data?.detail
                || e?.message
                || 'Could not load financing result.';
      setError(typeof msg === 'string' ? msg : 'Could not load financing result.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [clientId]);

  useEffect(() => {
    if (open && clientId) { load('cached'); }
    // Intentionally NOT clearing data on close so returning to the same
    // client is instant — the fetcher will reload on next open regardless.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, clientId]);

  const resultType   = data?.result_type || 'NOT_SUBMITTED';
  const displayLabel = data?.result_display_label || STATE_STYLES[resultType]?.label || resultType;
  const message      = data?.result_message || '';
  const offers       = Array.isArray(data?.offers) ? data.offers : [];
  const validOffers  = offers.filter(o => {
    const raw = o?.approved_amount ?? o?.approvedAmount ?? o?.amount ?? 0;
    const n = typeof raw === 'string' ? parseFloat(raw.replace(/[$,]/g, '')) : Number(raw);
    return !Number.isNaN(n) && n > 0;
  });
  const style = STATE_STYLES[resultType] || STATE_STYLES.NOT_SUBMITTED;
  const StateIcon = style.Icon;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-2xl" data-testid="financing-result-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <StateIcon className="h-4 w-4" />
            Financing Result — {clientName || clientId}
          </DialogTitle>
          <DialogDescription className="sr-only">
            View this Client's current financing application state.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-10 flex items-center justify-center text-sm text-slate-500" data-testid="financing-result-loading">
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Loading current financing result…
          </div>
        ) : error ? (
          <div className="py-8 text-center space-y-2" data-testid="financing-result-error">
            <div className="text-sm text-red-700">{error}</div>
            <Button variant="outline" size="sm" onClick={() => load('cached')} data-testid="financing-result-retry">
              Retry
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Badge className={`${style.badge} border`} data-testid={`financing-state-badge-${resultType}`}>
                {displayLabel}
              </Badge>
              <Button
                variant="outline"
                size="sm"
                onClick={() => load('refresh')}
                disabled={refreshing}
                data-testid="financing-result-refresh"
              >
                {refreshing
                  ? <><Loader2 className="h-3 w-3 mr-1.5 animate-spin" /> Refreshing…</>
                  : <><RefreshCw className="h-3 w-3 mr-1.5" /> Refresh Status</>}
              </Button>
            </div>

            {message && (
              <div className="text-sm text-slate-700" data-testid="financing-result-message">
                {message}
              </div>
            )}

            {resultType === 'VALID_OFFER' && validOffers.length > 0 && (
              <div className="space-y-3" data-testid="financing-offers-list">
                {validOffers.map((offer, idx) => (
                  <OfferCard key={offer.offer_id || idx} index={idx + 1} offer={offer} />
                ))}
              </div>
            )}

            {resultType === 'NO_OFFER_FOUND' && (
              // v3.21-1760 · Explicitly render NO_OFFER_FOUND as a valid
              // completed financing result. Do NOT render any fake
              // "Offer 1" card, $0 approved amount, 0 payment, 0 term, 0
              // rate — those would be fabricated data.
              <div
                className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700"
                data-testid="financing-no-offer-panel"
              >
                No lender offer was returned for this application. This is a
                completed financing result — no fake offer will be shown.
              </div>
            )}

            {resultType === 'SUBMISSION_ERROR' && (
              <div
                className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900"
                data-testid="financing-submission-issue-panel"
              >
                Submission Issue. Financing submission could not be completed
                because lender availability is currently unavailable. This is
                NOT a lender decline — the application never reached a lender
                decision. Retry once the configuration issue is resolved.
              </div>
            )}

            {resultType === 'PROCESSING' && (
              <div
                className="rounded-lg border border-sky-200 bg-sky-50 p-4 text-sm text-sky-900"
                data-testid="financing-processing-panel"
              >
                Processing. Application is being processed. Offers will appear
                once available.
              </div>
            )}

            {resultType === 'NOT_SUBMITTED' && (
              <div
                className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-700"
                data-testid="financing-not-submitted-panel"
              >
                Not Submitted. Application has not been submitted to lenders yet.
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={() => onClose?.()} data-testid="financing-result-close">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
